import { normalize } from 'normalizr'

import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import api from '@functions/api'

import type { AgencyProviderDeadlineGroupModel } from '@models/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/agencyProviderDeadlineGroup/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/agencyProviderDeadlineGroup/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/agencyProviderDeadlineGroup/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/agencyProviderDeadlineGroup/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/agencyProviderDeadlineGroup/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/agencyProviderDeadlineGroup/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/agencyProviderDeadlineGroup/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/agencyProviderDeadlineGroup/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/agencyProviderDeadlineGroup/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/agencyProviderDeadlineGroup/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/agencyProviderDeadlineGroup/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/agencyProviderDeadlineGroup/DELETE_FAILURE'

export type AgencyProviderDeadlineGroupModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: AgencyProviderDeadlineGroupModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadAgencyProviderDeadlineGroups(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().agencyProviderDeadlineGroups.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)) {
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/agency_provider_deadline_groups.json', options)
      .then(({ data }: { data: AgencyProviderDeadlineGroupModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PROVIDER_DEADLINE_GROUP_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createAgencyProviderDeadlineGroup(
  agencyProviderDeadlineGroup: Partial<AgencyProviderDeadlineGroupModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      agency_provider_deadline_group: agencyProviderDeadlineGroup,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/agency_provider_deadline_groups.json', options, config)
      .then(({ data }: { data: AgencyProviderDeadlineGroupModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PROVIDER_DEADLINE_GROUP)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateAgencyProviderDeadlineGroup(
  agencyProviderDeadlineGroup: UpdateParams<AgencyProviderDeadlineGroupModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      agency_provider_deadline_group: agencyProviderDeadlineGroup,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agency_provider_deadline_groups/${agencyProviderDeadlineGroup.id}.json`, options, config)
      .then(({ data }: { data: AgencyProviderDeadlineGroupModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PROVIDER_DEADLINE_GROUP)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteAgencyProviderDeadlineGroup(
  agencyProviderDeadlineGroup: DeleteParams<AgencyProviderDeadlineGroupModel>,
) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/agency_provider_deadline_groups/${agencyProviderDeadlineGroup.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_PROVIDER_DEADLINE_GROUP)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: AgencyProviderDeadlineGroupModuleState = initialState,
  action: Action = { type: '' },
): AgencyProviderDeadlineGroupModuleState {
  switch (action.type) {
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
